<template>
  <div>
    <div class="card">
      <div class="card-header py-2">
        {{ $t('offlineMaps') }}
      </div>
      <div class="card-body p-2">
        <div class="buttongroup">
          {{ $t('zoomFrom') }} <select v-model="minimalZoom">
            <option v-for="zoom in allowMinimalZoom" :value="zoom" :key="zoom">{{ zoom }}</option>
          </select>
          {{ $t('zoomTo') }} <select v-model="maximalZoom">
            <template v-for="zoom in allowMinimalZoom" :value="zoom" :key="zoom">
              <option v-if="zoom >= minimalZoom" :value="zoom">{{ zoom }}</option>
            </template>
          </select>
        </div>
        <div class="buttongroup">
          {{ $t('threads') }} <select v-model="downloadThreads">
            <option v-for="thread in allowDownloadThreads" :value="thread" :key="thread">{{ thread }}</option>
          </select>&nbsp;
          <b-form-checkbox class="display-inline marginright5" v-model="useCache"></b-form-checkbox>
          <span @click="useCache = !useCache">{{ $t('useCache') }}</span>
          <button :disabled="operationInProgess" class="btn btn-primary btn-sm" @click="downloadOffline">
            <img style='height: 16px; animation: rotation 2s infinite linear;' v-if="saving" :src="icons.spinnerWhite" />&nbsp;
            {{ $t('downloadOffline') }}
          </button>&nbsp;
          <span va-b-tooltip.hover :title="$t('corsWarning')">
            <img style="height: 24px; cursor: pointer;" :src="icons.info" />
          </span>
          <template v-if="saving">
            <br>
            <button class="btn btn-danger btn-sm" @click="stopDownloadOffline">
              {{ $t('stopDownloadOffline') }}
            </button>
          </template>
        </div>
        <div class="buttongroup">
          <button :disabled="operationInProgess" class="btn btn-primary btn-sm" @click="clearOffline">
            <img style='height: 16px; animation: rotation 2s infinite linear;' v-if="clearing" :src="icons.spinnerWhite" />&nbsp;
            {{ $t('clearOffline') }}
          </button>&nbsp;
          <button :disabled="operationInProgess" class="btn btn-primary btn-sm" @click="countGlobalOffline">
            <img style='height: 16px; animation: rotation 2s infinite linear;' v-if="countingGlobal" :src="icons.spinnerWhite" />&nbsp;
            {{ $t('countGlobalOffline') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import BaseComponent from './Base.vue';
import $ from 'jquery';
import { type TranslateResult } from 'vue-i18n';
import { defineComponent } from "vue";
import { createSaveTilesControl } from '@/ts/leafletoffline';

export default defineComponent({
  extends: BaseComponent,
  data() {
    return {
      allowMinimalZoom: [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18],
      minimalZoom: 5,
      maximalZoom: 18,
      saving: false,
      deleting: false,
      clearing: false,
      countingGlobal: false,
      layerName: '',
      allowDownloadThreads: [1, 3, 5, 10, 20, 30, 40, 50],
      downloadThreads: 10,
      useCache: true,
    };
  },
  computed: {
    operationInProgess() {
      return this.deleting || this.saving || this.clearing || this.countingGlobal;
    },
  },
  created() {
    window.mainmap.on('baselayerchange', this.onBaseLayerChange);
    window.offlineControl = createSaveTilesControl(window.mainmap, null, {
      zoomlevels: this.getOfflineZooms(),
      alwaysDownload: true,
      parallel: 5,
    });
  },
  methods: {
    getOfflineZooms() {
      const zooms = [];
      for (let i = this.minimalZoom; i <= this.maximalZoom; i++) {
        zooms.push(i);
      }
      return zooms;
    },
    setMessageClass(className: string) {
      document.getElementById('messageClassTop').className = 'alert border border-dark ' + className;
    },
    showMessage(message: string | TranslateResult) {
      document.getElementById('messageTop').innerHTML = (message as string);
    },
    showMessageDiv(message: string | TranslateResult) {
      document.getElementById('messageDivTop').style.display = 'block';
      this.showMessage(message);
      this.setMessageClass('alert-success');
    },
    showMessageError(message: string | TranslateResult) {
      document.getElementById('messageDivTop').style.display = 'block';
      this.showMessage(message);
      this.setMessageClass('alert-danger');
    },
    openImportFileInput() {
      $('#importFileInputOffline').click();
    },
    downloadOffline() {
      if (this.layerName.endsWith('Offline')) {
        this.saving = true;
        // @ts-ignore
        window.offlineControl.options.parallel = this.downloadThreads;
        // @ts-ignore
        window.offlineControl.options.alwaysDownload = !this.useCache;
        // @ts-ignore
        window.offlineControl.options.layerName = this.layerName;
        // @ts-ignore
        const firstUrl = window.offlineControl.baseLayer._url;
        // @ts-ignore
        window.offlineControl.options.zoomlevels = this.getOfflineZooms();
        const countToSave = window.offlineControl.calculateTiles(true) as number;
        // @ts-ignore
        if (window.confirm(this.$t('saveAllTitles', [countToSave, window.offlineControl.options.zoomlevels.join(", "), firstUrl, this.useCache]))) {
          setTimeout(() => window.offlineControl.downloadStart(), 100);
        } else {
          this.saving = false;
        }
      } else {
        this.showMessageError(this.$t('mapNotOffline'));
      }
    },
    stopDownloadOffline() {
      window.offlineControl.downloadCancel();
      this.saving = false;
    },
    showProgressMessage(status: any) {
      if (status.lengthLoadErrors) {
        const processed = status.lengthSaved + status.lengthLoadedFromCache + status.lengthLoadErrors;
        this.showMessageError('' + processed + '/' + status.lengthToBeSaved + ' (cache: ' + status.lengthLoadedFromCache + ') (' + this.$t('bitmapDownloadErrors') + ': ' + status.lengthLoadErrors + ')');
      } else {
        const processed = status.lengthSaved + status.lengthLoadedFromCache;
        this.showMessage('' + processed + '/' + status.lengthToBeSaved + ' (cache: ' + status.lengthLoadedFromCache + ')');
      }
    },
    onBaseLayerChange(e: L.LayersControlEvent) {
      this.layerName = e.name;
      if (this.layerName.endsWith('Offline')) {
        window.offlineControl.setLayer(this.$store.state.baseMaps[this.layerName]);
        window.offlineControl.baseLayer.on('savestart', (status: any) => {
          this.showMessageDiv('' + status.lengthSaved + '/' + status.lengthToBeSaved);
        });
        window.offlineControl.baseLayer.on('refreshstatus', (status) => {
          this.showProgressMessage(status);
        });
        window.offlineControl.baseLayer.on('saveend', (status) => {
          this.showProgressMessage(status);
          this.saving = false;
        });
      }
    },
    onMinimalZoomChanged() {
      if (this.maximalZoom < this.minimalZoom) {
        this.maximalZoom = this.minimalZoom;
      }
    },
    async clearOffline() {
      this.clearing = true;
      const sum = await window.offlineControl.getStorageLength();
      if (window.confirm(this.$t('removeAllTitles', [sum]))) {
        const result = await window.offlineControl.storageTruncate();
        this.showMessageDiv(this.$t('allBitmapsRemoved'));
        this.clearing = false;
      } else {
        this.clearing = false;
      }
    },
    async countGlobalOffline() {
      this.countingGlobal = true;
      const result = await window.offlineControl.getStorageLength();
      this.showMessageDiv(String(result));
      this.countingGlobal = false;
    },
  },
  watch: {
    "minimalZoom": [{handler: "onMinimalZoomChanged"}],
  }
})


</script>

<style>
.buttongroup {
  border-radius: 5px 5px 5px;
  border: 1px solid gray;
  padding: 8px;
  margin-bottom: 10px;
}
</style>